import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CheckCircle2, Download, Mail, AlertCircle } from 'lucide-react';
import { mockData } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EBookModal = ({ isOpen, onClose, source = 'construction_page', title, description, chapters }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/ebook/download`, {
        name,
        email,
        source
      });

      if (response.data.success) {
        setSubmitted(true);
        
        // Reset after 3 seconds
        setTimeout(() => {
          setSubmitted(false);
          setEmail('');
          setName('');
          onClose();
        }, 3000);
      }
    } catch (err) {
      console.error('E-book download error:', err);
      setError('Failed to process request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[rgb(0,55,32)]">
                {title || mockData.ebook.title}
              </DialogTitle>
              <DialogDescription className="text-base text-[rgb(131,146,140)] mt-2">
                {description || mockData.ebook.description}
              </DialogDescription>
            </DialogHeader>

            <div className="my-4">
              <h4 className="font-semibold text-[rgb(0,55,32)] mb-3">What's Inside:</h4>
              <ul className="space-y-2">
                {(chapters || mockData.ebook.chapters).map((chapter, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-[rgb(14,15,12)]">
                    <CheckCircle2 className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                    <span>{chapter}</span>
                  </li>
                ))}
              </ul>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-[rgb(0,55,32)]">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  required
                  disabled={loading}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-[rgb(0,55,32)]">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  disabled={loading}
                  className="mt-1"
                />
              </div>
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold h-12 rounded-full hover:scale-102 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    Download Free E-Book
                  </>
                )}
              </Button>
              <p className="text-xs text-[rgb(175,183,180)] text-center">
                We respect your privacy. Your email will never be shared.{' '}
                <a href="/privacy-policy" className="underline hover:text-[rgb(0,55,32)]">Privacy Policy</a>
              </p>
            </form>
          </>
        ) : (
          <div className="py-8 text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="w-16 h-16 text-[var(--accent-primary)]" />
            </div>
            <h3 className="text-2xl font-bold text-[rgb(0,55,32)] mb-2">Success!</h3>
            <p className="text-[rgb(131,146,140)]">
              Check your email for the download link.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EBookModal;
